import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import PageLoader from 'src/UI/loader/PageLoader';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import React, { useState } from 'react';
import { useEntity } from 'src/financial/financial-data/hooks/useFinancialItem';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { ClientListItem } from 'src/admin/users/types';
import { FinancialItemDataDynamicForm } from 'src/financial/financial-data/financial-item/FinancialItemDataDynamicForm';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useContentMeta } from 'src/UI/app-container';
import { ChatMode } from 'src/UI/app-container/chat/types';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  financialsReturnPath,
  RETURN_TO_PARAM,
} from 'src/financial/financial-data/financial-item/rework/returnPath';

type Params = {
  id: string;
  product: string;
  clientId: string;
};

export default function ViewAndEditFinancialItemPage() {
  const { product, id } = useParams<Params>();
  const { client, clientId } = useCurrentClientIfAvailable();
  useContentMeta({ pageType: 'client', entityId: clientId, chatMode: ChatMode.CLIENT });

  if (product && id && client) {
    return <ViewAndEditFinancialItem product={product} id={id} client={client} />;
  }

  return null;
}

type Props = {
  id: string;
  product: string;
  client: ClientListItem;
};

const ViewAndEditFinancialItem = ({ product, id, client }: Props) => {
  const {
    isLoading: isLoadingEntityData,
    refetch: refetchEntityData,
    entityData,
    error,
    isError,
  } = useEntity({ id, clientId: client.id, entityType: product });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { flagEnabledFinancialRework } = useFlags();

  const { deleteEntityByTypeAndId, isLoading: isDeleting } = useDeleteEntities();
  const [isRefetchingAfterSave, setIsRefetchingAfterSave] = useState(false);

  const isLoading = isLoadingEntityData || !entityData || isDeleting || isRefetchingAfterSave;

  if (isError) {
    return <GenericErrorPage error={error} />;
  }

  const handleDeletePress = async () => {
    try {
      await deleteEntityByTypeAndId(client.id, product, id);
      navigate(financialsReturnPath(client.id, searchParams.get(RETURN_TO_PARAM)));
    } catch (e) {
      notifyAndLogError(
        'Error deleting item',
        'Something went wrong trying to delete the item. Please try again and contact support if the issue persists.',
        e,
        { product, id },
      );
    }
  };

  const refetchAfterChange = async () => {
    setIsRefetchingAfterSave(true);
    try {
      await refetchEntityData();
    } finally {
      setIsRefetchingAfterSave(false);
    }
  };

  // A saved item has nothing more to say on its own page, so it hands the adviser back to the
  // Financials they came from rather than leaving them in a form they have finished with.
  const handleSuccessfulSave = flagEnabledFinancialRework
    ? () => navigate(financialsReturnPath(client.id, searchParams.get(RETURN_TO_PARAM)))
    : refetchAfterChange;

  return isLoading ? (
    <PageLoader />
  ) : (
    <FinancialItemDataDynamicForm
      product={product}
      client={client}
      isCreateMode={false}
      entityWithMetadata={entityData}
      onDeleteConfirm={handleDeletePress}
      onSuccessfulSave={handleSuccessfulSave}
      onAssetConnected={refetchAfterChange}
    />
  );
};
